/**
 * Deterministic tender readiness from domain fields (deadline-aware).
 * Prefer READY / BLOCKED / IN_PROGRESS — no invented %.
 */
export type TenderReadinessState = 'READY' | 'BLOCKED' | 'IN_PROGRESS';

export type TenderReadinessCheck = {
  id: string;
  group: string;
  label: string;
  state: TenderReadinessState;
  detail?: string;
};

export type TenderReadinessGroup = {
  name: string;
  state: TenderReadinessState;
  checks: TenderReadinessCheck[];
};

export type TenderReadinessResult = {
  overall: TenderReadinessState;
  checks: TenderReadinessCheck[];
  groups: TenderReadinessGroup[];
};

const TERMINAL = new Set(['awarded', 'closed', 'cancelled', 'withdrawn', 'lost']);

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function rollup(states: TenderReadinessState[]): TenderReadinessState {
  if (states.some((s) => s === 'BLOCKED')) return 'BLOCKED';
  if (states.length && states.every((s) => s === 'READY')) return 'READY';
  return 'IN_PROGRESS';
}

function parseDue(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function buildTenderReadiness(
  row: Record<string, unknown>,
  opts?: {
    today?: Date;
    bidDecisionCount?: number;
    approvedGoCount?: number;
    pendingApprovalCount?: number;
  }
): TenderReadinessResult {
  const today = startOfDay(opts?.today ?? new Date());
  const title = asStr(row.title);
  const client = asStr(row.client_name || row.clientName);
  const opportunityId = asStr(row.opportunity_id || row.opportunityId);
  const currency = asStr(row.currency);
  const model = asStr(row.commercial_model || row.commercialModel);
  const status = asStr(row.status || 'Draft').toLowerCase();
  const isTerminal = TERMINAL.has(status);
  const bidDueRaw = asStr(row.bid_due_at || row.bidDueAt);
  const subDueRaw = asStr(row.submission_due_at || row.submissionDueAt);

  const checks: TenderReadinessCheck[] = [];

  checks.push({
    id: 'identity-title',
    group: 'Identity',
    label: 'Title',
    state: title ? 'READY' : 'BLOCKED',
    detail: title ? undefined : 'Title required',
  });
  checks.push({
    id: 'identity-client',
    group: 'Identity',
    label: 'Client',
    state: client ? 'READY' : 'IN_PROGRESS',
    detail: client ? undefined : 'Client not set',
  });

  checks.push({
    id: 'link-opportunity',
    group: 'Commercial linkage',
    label: 'Opportunity linked',
    state: opportunityId ? 'READY' : 'IN_PROGRESS',
    detail: opportunityId ? undefined : 'Link opportunity for pursuit continuity',
  });

  checks.push({
    id: 'commercial-model',
    group: 'Commercial setup',
    label: 'Commercial model',
    state: model ? 'READY' : 'IN_PROGRESS',
    detail: model || 'Model not set',
  });
  checks.push({
    id: 'commercial-currency',
    group: 'Commercial setup',
    label: 'Currency',
    state: currency ? 'READY' : 'IN_PROGRESS',
  });

  const bidDue = parseDue(bidDueRaw);
  if (!bidDue) {
    checks.push({
      id: 'deadline-bid',
      group: 'Deadlines',
      label: 'Bid due',
      state: 'IN_PROGRESS',
      detail: 'Bid due date not set',
    });
  } else if (!isTerminal && bidDue < today) {
    checks.push({
      id: 'deadline-bid',
      group: 'Deadlines',
      label: 'Bid due',
      state: 'BLOCKED',
      detail: 'Bid due date has passed',
    });
  } else {
    checks.push({
      id: 'deadline-bid',
      group: 'Deadlines',
      label: 'Bid due',
      state: 'READY',
      detail: bidDueRaw.slice(0, 10),
    });
  }

  const subDue = parseDue(subDueRaw);
  if (!subDue) {
    checks.push({
      id: 'deadline-submission',
      group: 'Deadlines',
      label: 'Submission due',
      state: 'IN_PROGRESS',
      detail: 'Submission due not set',
    });
  } else if (!isTerminal && subDue < today) {
    checks.push({
      id: 'deadline-submission',
      group: 'Deadlines',
      label: 'Submission due',
      state: 'BLOCKED',
      detail: 'Submission due date has passed',
    });
  } else {
    checks.push({
      id: 'deadline-submission',
      group: 'Deadlines',
      label: 'Submission due',
      state: 'READY',
      detail: subDueRaw.slice(0, 10),
    });
  }

  const bidCount = opts?.bidDecisionCount ?? 0;
  const goCount = opts?.approvedGoCount ?? 0;
  const pendingAppr = opts?.pendingApprovalCount ?? 0;

  if (pendingAppr > 0) {
    checks.push({
      id: 'bid-decision',
      group: 'Bid decision',
      label: 'Approval pending',
      state: 'IN_PROGRESS',
      detail: `${pendingAppr} bid decision(s) awaiting approval`,
    });
  } else if (goCount > 0) {
    checks.push({
      id: 'bid-decision',
      group: 'Bid decision',
      label: 'GO approved',
      state: 'READY',
    });
  } else if (bidCount > 0) {
    checks.push({
      id: 'bid-decision',
      group: 'Bid decision',
      label: 'Bid decision on file',
      state: 'IN_PROGRESS',
      detail: 'No approved GO yet',
    });
  } else {
    checks.push({
      id: 'bid-decision',
      group: 'Bid decision',
      label: 'Bid decision',
      state: 'IN_PROGRESS',
      detail: 'No bid decision yet',
    });
  }

  if (isTerminal) {
    checks.push({
      id: 'status',
      group: 'Outcome',
      label: `Status: ${asStr(row.status)}`,
      state: 'READY',
      detail: 'Terminal tender status',
    });
  } else {
    checks.push({
      id: 'status',
      group: 'Outcome',
      label: `Status: ${asStr(row.status) || 'Draft'}`,
      state: 'IN_PROGRESS',
    });
  }

  const groupNames = Array.from(new Set(checks.map((c) => c.group)));
  const groups = groupNames.map((name) => {
    const gChecks = checks.filter((c) => c.group === name);
    return { name, state: rollup(gChecks.map((c) => c.state)), checks: gChecks };
  });

  return {
    overall: rollup(checks.map((c) => c.state)),
    checks,
    groups,
  };
}

export function readinessTagColor(state: TenderReadinessState): string {
  switch (state) {
    case 'READY':
      return 'success';
    case 'BLOCKED':
      return 'error';
    default:
      return 'processing';
  }
}
